import {
  createTheme as muiCreateTheme,
  PaletteOptions,
  Theme,
} from "@mui/material";
import ThemeProvider, {
  ThemeProviderProps,
} from "@mui/material/styles/ThemeProvider";
import { radius, components } from "./const";

export type ThemeMode = "dark";
export const TypedThemeProvider = (props: ThemeProviderProps<Theme>) =>
  ThemeProvider<Theme>(props);

export const createTheme = (mode: ThemeMode = "dark") => {
  const palette = palettes[mode];
  return muiCreateTheme({
    palette,
    radius,
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 1280,
        lg: 1920,
        xl: 2560,
      },
    },
    typography: {
      fontFamily: "Rubik",
      allVariants: {
        textTransform: "none",
      },
    },
    components,
    shape: {
      borderRadius: 12,
    },
  });
};

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
  }
}

export const darkPalette: PaletteOptions = {
  mode: "dark",
  background: {
    dark: "#12141C",
    default: "#191C28",
    paper: "#242734",
    highlight: "#30323D",
    highlighter: "#3D404A",
  },
  error: {
    main: "#FF3A41",
    light: "#FF545A",
    dark: "#B3171C",
  },
  success: {
    main: "#00D66D",
    light: "#19FF8C",
    dark: "#008A45",
  },
  warning: {
    main: "#FF7046",
    light: "#FF845E",
    dark: "#B3411E",
  },
  info: {
    main: "#3183FF",
    light: "#4A4DFF",
    dark: "#1114B3",
  },
  ticket: {
    darker: "#503538",
    faded: "#FF7046",
    main: "#FF7046",
    Critical: {
      main: "#D9394A",
      light: "#FFB2BB",
      dark: "#1A0A04",
    },
    Major: {
      main: "#FF9514",
      light: "#FFDDB2",
      dark: "#1F1100",
    },
    Minor: {
      main: "#FEF216",
      light: "#FFFBB3",
      dark: "#1E1D00",
    },
    Informational: {
      main: "#0288D1",
      light: "#BFDBF3",
      dark: "#05101A",
    },
  },
  device: {
    label: "#DDDEFE",
    border: "#4F587D",
    faded: "#4D4C52",
    main: "#4D4C52",
    dark: "#2f2e32",
  },
  meeting: {
    label: "#B6F0ED",
    background: "#5A938F",
    dark: "#1B4947",
    darker: "#36504F",
  },
  deckboard: {
    orange: { main: "#FF7046", light: "#FF845E", dark: "#B3411E" },
    green: { main: "#7DB16C", light: "#C6FCB3", dark: "#3F6333" },
    blue: { main: "#4666A3", light: "#7FA6F0", dark: "#1D3157" },
    lightblue: { main: "#DDDEFE", light: "#F7F8FF", dark: "#898BB3" },
    grey: { main: "#4D4C52", light: "#95939E", dark: "#353438" },
    purple: { main: "#B81496", light: "#FF36D7", dark: "#6B0657" },
    cyan: { main: "#68BDF6", light: "#85CEFF", dark: "#367BA8" },
  },
  graph: {
    pink: "#915981",
    purple: "#685991",
    blue: "#596391",
    teal: "#5A8593",
    green: "#61935A",
    mustard: "#935A5A",
  },
  common: {
    grey: {
      light: "#9B9B9B",
      dark: "#707070",
      gE6E6E6: "#E6E6E6",
      gBFBFBF: "#BFBFBF",
      g9B9B9B: "#9B9B9B",
      g828282: "#828282",
      g5C5C5C: "#5C5C5C",
      g474a53: "#474a53",
      g26272D: "#26272D",
    },
    purple: {
      main: "#A451FF",
    },
  },
  trigger: {
    background: "rgba(206, 68, 83, 0.1)",
  },
  text: {
    errorGrey: "#191C28",
  },
  primary: {
    main: "#90CAF9",
    light: "#B3D6F2",
    dark: "#6487A8",
  },
};

export const palettes: { [K in ThemeMode]: PaletteOptions } = {
  dark: darkPalette,
};
